'use client';

import { withBase } from './lib/brand';
import { useEffect } from 'react';

/**
 * App-level error boundary. Without this, any client-side render error (e.g. an
 * intermittent race in the trip/route view) crashed the WHOLE app to a blank
 * "Application error" white screen. This contains it to a recoverable fallback
 * and logs the real (unminified) message so the cause is diagnosable.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Surface the real message/stack (the Error object's message is NOT minified)
    // so intermittent client crashes can actually be diagnosed.
    // eslint-disable-next-line no-console
    console.error('[AppError]', error?.message, '| digest:', error?.digest, '\n', error?.stack);
    // Best-effort beacon so we capture real-user occurrences server-side.
    try {
      const body = JSON.stringify({
        message: error?.message,
        digest: error?.digest,
        stack: error?.stack,
        url: typeof window !== 'undefined' ? window.location.href : '',
        ua: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      });
      navigator.sendBeacon?.('https://app.trip-planner.com.au/api/client-error', body);
    } catch {
      /* never let logging break the fallback */
    }
  }, [error]);

  return (
    <div
      style={{
        minHeight: '50vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        textAlign: 'center',
        fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif',
      }}
    >
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: '#002A4E' }}>
        Something went wrong loading this view
      </h2>
      <p style={{ color: '#5B7184', marginBottom: 16, maxWidth: 420 }}>
        Please try again — the rest of the site is unaffected.
      </p>
      <div style={{ display: 'flex', gap: 12 }}>
        <button
          onClick={() => reset()}
          style={{
            background: '#0067B1',
            color: '#fff',
            border: 0,
            borderRadius: 8,
            padding: '10px 18px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Try again
        </button>
        <a
          href={withBase("")}
          style={{
            border: '1px solid #cdd9e4',
            borderRadius: 8,
            padding: '10px 18px',
            fontWeight: 600,
            color: '#0067B1',
            textDecoration: 'none',
          }}
        >
          Back to search
        </a>
      </div>
    </div>
  );
}
